import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';
import TreeSidebar from '../components/TreeSidebar';
import SiteManager from '../components/SiteManager';
import CameraManager from '../components/CameraManager';
import { projectsAPI, zonesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Plus, Pen, Trash2, Crosshair } from 'lucide-react';

// Removed Zones.css import

export default function Zones() {
  const navigate = useNavigate();
  const {
    selectedProject,
    selectedSite,
    selectedCamera,
    selectProject,
    selectSite,
    selectCamera,
  } = useProject();

  const [projects, setProjects] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Modal states
  const [showSiteManager, setShowSiteManager] = useState(false);
  const [showCameraManager, setShowCameraManager] = useState(false);

  // Load projects on mount
  useEffect(() => {
    loadProjects();
  }, []);

  // Load zones when camera is selected
  useEffect(() => {
    if (selectedCamera) {
      loadZones(selectedCamera.id);
    } else {
      setZones([]);
    }
  }, [selectedCamera]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const result = await projectsAPI.getAll({ limit: 1000 });
      if (result.success) {
        setProjects(result.projects || []);
      }
    } catch (err) {
      console.error('Failed to load projects:', err);
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const loadZones = async (cameraId) => {
    try {
      setLoading(true);
      const result = await zonesAPI.getAll(cameraId, { limit: 100 });
      setZones(result.zones || []);
    } catch (err) {
      console.error('Failed to load zones:', err);
      setError('Failed to load zones');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (value) => {
    const project = projects.find((p) => p.id === value);
    selectProject(project || null);
  };

  const handleSiteSelect = (site) => {
    selectSite(site);
    setShowSiteManager(false);
  };

  const handleCameraSelect = (camera, site) => {
    if (site) {
      selectSite(site);
    }
    selectCamera(camera);
  };

  const handleZoneDelete = async (zoneId) => {
    if (!window.confirm('Are you sure you want to delete this zone?')) {
      return;
    }

    try {
      await zonesAPI.delete(zoneId);
      if (selectedCamera) {
        loadZones(selectedCamera.id);
      }
    } catch (err) {
      setError(err.message || 'Failed to delete zone');
    }
  };

  const handleDrawZones = () => {
    if (!selectedCamera) {
      setError('Please select a camera first');
      return;
    }

    navigate('/zones/editor', {
      state: {
        cameraId: selectedCamera.id,
        cameraName: selectedCamera.name,
      },
    });
  };

  const getBadgesForZone = (zone) => {
    const type = zone.zone_type;
    let variant = 'secondary';
    let label = type.replace(/_/g, ' ');

    if (type === 'line_crossing') variant = 'default';
    if (type === 'roi_filtering') variant = 'outline';
    if (type === 'overcrowding') variant = 'destructive';

    return <Badge variant={variant} className="uppercase text-[10px]">{label}</Badge>;
  };

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] bg-background">
      <div className="px-6 py-4 border-b bg-card space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">Zone Management</h1>
        <p className="text-muted-foreground">Draw and configure detection zones for your cameras</p>
      </div>

      {error && (
        <div className="m-4 p-4 bg-destructive/10 text-destructive border border-destructive/20 rounded-md flex justify-between items-center">
          <span>{error}</span>
          <Button variant="ghost" size="sm" onClick={() => setError('')} className="h-6 w-6 p-0 hover:bg-destructive/20">
            ×
          </Button>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar - Project Selection & Tree */}
        <div className="w-80 border-r flex flex-col bg-background">
          <div className="p-4 border-b space-y-3">
            <div className="space-y-1.5">
              <span className="text-sm font-medium">Select Project</span>
              <Select
                value={selectedProject?.id || ''}
                onValueChange={handleProjectChange}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedProject && (
              <p className="text-xs text-muted-foreground line-clamp-2">
                {selectedProject.description}
              </p>
            )}
          </div>

          {selectedProject && (
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="flex-1 overflow-auto">
                <TreeSidebar
                  currentProject={selectedProject}
                  currentSite={selectedSite}
                  currentCamera={selectedCamera}
                  onSiteSelect={handleSiteSelect}
                  onCameraSelect={handleCameraSelect}
                  onAddSite={() => setShowSiteManager(true)}
                  onAddCamera={() => setShowCameraManager(true)}
                />
              </div>

              {/* Quick Actions */}
              <div className="p-3 border-t bg-card space-y-2">
                <Button variant="secondary" className="w-full justify-start h-8" onClick={() => setShowSiteManager(true)}>
                  <Plus className="mr-2 h-4 w-4" /> Add Site
                </Button>
                {selectedSite && (
                  <Button variant="secondary" className="w-full justify-start h-8" onClick={() => setShowCameraManager(true)}>
                    <Plus className="mr-2 h-4 w-4" /> Add Camera
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Main Content - Zones */}
        <div className="flex-1 overflow-y-auto bg-muted/5 p-6">
          {!selectedProject ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-8 text-muted-foreground">
              <div className="h-16 w-16 bg-muted rounded-full flex items-center justify-center mb-4">
                <span className="text-3xl">📁</span>
              </div>
              <h2 className="text-xl font-semibold text-foreground">No Project Selected</h2>
              <p className="mt-2">Select a project from the sidebar to view sites and cameras</p>
            </div>
          ) : !selectedCamera ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-8 text-muted-foreground">
              <div className="h-16 w-16 bg-muted rounded-full flex items-center justify-center mb-4">
                <span className="text-3xl">📹</span>
              </div>
              <h2 className="text-xl font-semibold text-foreground">No Camera Selected</h2>
              <p className="mt-2 text-sm max-w-sm mb-6">Select a camera from the tree to view and manage its zones</p>
              {!selectedSite && (
                <Button onClick={() => setShowSiteManager(true)}>
                  Create Your First Site
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-6 max-w-5xl mx-auto">
              {/* Camera Info Header */}
              <Card>
                <CardContent className="p-6 flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-semibold flex items-center gap-2">
                        {selectedCamera.name}
                      </h2>
                      <Badge variant="outline" className="font-mono text-xs">{selectedCamera.camera_id}</Badge>
                    </div>
                    <div className="text-sm text-muted-foreground flex flex-col gap-1">
                      {selectedCamera.location && (
                        <span className="flex items-center gap-1">📍 {selectedCamera.location}</span>
                      )}
                      {selectedSite && (
                        <span>Site: {selectedSite.name} {selectedSite.city && <span className="text-xs ml-1">• {selectedSite.city}</span>}</span>
                      )}
                    </div>
                  </div>
                  <Button size="lg" onClick={handleDrawZones}>
                    <Pen className="mr-2 h-4 w-4" /> Draw Zones
                  </Button>
                </CardContent>
              </Card>

              {/* Zones List */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">Configured Zones ({zones.length})</h3>
                </div>

                {loading ? (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <Loader2 className="h-8 w-8 animate-spin mb-2" />
                    <p>Loading zones...</p>
                  </div>
                ) : zones.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center mb-4 text-primary">
                        <Crosshair className="h-6 w-6" />
                      </div>
                      <h4 className="text-lg font-medium mb-1">No Zones Configured</h4>
                      <p className="text-muted-foreground text-sm mb-4 max-w-xs">Start by drawing zones on the camera's image to detect objects, people, or events.</p>
                      <Button onClick={handleDrawZones}>
                        <Pen className="mr-2 h-4 w-4" /> Draw First Zone
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {zones.map((zone) => (
                      <Card key={zone.id} className="hover:border-primary/50 transition-colors">
                        <CardHeader className="pb-2 space-y-1">
                          <div className="flex justify-between items-start gap-2">
                            <CardTitle className="text-base font-medium truncate">
                              {zone.name || `Zone ${zone.zone_index + 1}`}
                            </CardTitle>
                            {getBadgesForZone(zone)}
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between py-1 border-b border-dashed">
                              <span className="text-muted-foreground">Points</span>
                              <span className="font-mono">{zone.points?.length || 0}</span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-dashed">
                              <span className="text-muted-foreground">Status</span>
                              <span className={zone.enabled ? "text-green-600 font-medium" : "text-muted-foreground"}>
                                {zone.enabled ? 'Enabled' : 'Disabled'}
                              </span>
                            </div>
                            <div className="flex justify-between py-1 border-b border-dashed">
                              <span className="text-muted-foreground">Index</span>
                              <span className="font-mono text-muted-foreground">{zone.zone_index}</span>
                            </div>
                          </div>

                          <div className="pt-4 flex justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 px-2"
                              onClick={() => handleZoneDelete(zone.id)}
                            >
                              <Trash2 className="h-4 w-4 mr-1.5" /> Delete
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dialog Managers */}
      {showSiteManager && (
        <SiteManager
          projectId={selectedProject?.id}
          isOpen={showSiteManager}
          onClose={() => setShowSiteManager(false)}
          onSiteCreated={handleSiteSelect}
        />
      )}

      {showCameraManager && (
        <CameraManager
          projectId={selectedProject?.id}
          siteId={selectedSite?.id}
          isOpen={showCameraManager}
          onClose={() => setShowCameraManager(false)}
          onCameraCreated={(camera) => {
            handleCameraSelect(camera, selectedSite);
            setShowCameraManager(false);
          }}
        />
      )}
    </div>
  );
}
